import { useState, useMemo, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ExternalLink, Github, Image as ImageIcon, Trash2, Sparkles, Edit2, Plus } from 'lucide-react'
import { AIProjectDescGenerator, type GeneratedProject } from '@/components/ai'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/constants/routes'
import { dispatchProjectsUpdated } from '@/services/projectSkillSync'

const PROJECTS_STORAGE_KEY = 'portfolio-projects'
const PROJECTS_VERSION_KEY = 'portfolio-projects-version'
const CURRENT_VERSION = 3  // バージョン3: デフォルトプロジェクトを削除

export interface Project {
  id: string
  title: string
  description: string
  techStack: string[]
  githubUrl: string
  demoUrl: string | null
  imageUrl?: string
  isAIGenerated?: boolean  // AI生成かどうか
}

// プロジェクトをlocalStorageから読み込む
function loadProjects(): Project[] {
  const version = localStorage.getItem(PROJECTS_VERSION_KEY)
  const saved = localStorage.getItem(PROJECTS_STORAGE_KEY)

  // バージョン3以降は空の状態から開始
  if (!version || parseInt(version) < CURRENT_VERSION) {
    localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify([]))
    localStorage.setItem(PROJECTS_VERSION_KEY, CURRENT_VERSION.toString())
    return []
  }

  if (!saved) {
    return []
  }

  try {
    return JSON.parse(saved) as Project[]
  } catch {
    return []
  }
}

// プロジェクトをlocalStorageに保存
function saveProjects(projects: Project[]): void {
  localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(projects))
  localStorage.setItem(PROJECTS_VERSION_KEY, CURRENT_VERSION.toString())
  // スキルページに変更を通知
  dispatchProjectsUpdated()
}

export function Projects() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTech = searchParams.get('tech')
  const [selectedTech, setSelectedTech] = useState<string | null>(initialTech)
  const [projects, setProjects] = useState<Project[]>([])
  const [editingProject, setEditingProject] = useState<Project | null>(null)

  // localStorageからプロジェクトを読み込む
  useEffect(() => {
    setProjects(loadProjects())
  }, [])

  // URLパラメータが変わったら状態を更新
  useEffect(() => {
    const tech = searchParams.get('tech')
    setSelectedTech(tech)
  }, [searchParams])

  // 全ての技術スタックを抽出
  const allTechStacks = useMemo(() => {
    return Array.from(
      new Set(projects.flatMap(p => p.techStack))
    ).sort()
  }, [projects])

  // フィルター変更時にURLパラメータも更新
  const handleTechSelect = (tech: string | null) => {
    setSelectedTech(tech)
    if (tech) {
      setSearchParams({ tech })
    } else {
      setSearchParams({})
    }
  }

  // プロジェクト追加（AI生成）
  const handleAddProject = useCallback((project: GeneratedProject) => {
    const newProject: Project = {
      id: `ai-${Date.now()}`,
      title: project.title,
      description: project.description,
      techStack: project.techStack,
      githubUrl: project.githubUrl,
      demoUrl: null,
      isAIGenerated: true,
    }

    setProjects(prev => {
      const updated = [...prev, newProject]
      saveProjects(updated)
      return updated
    })
  }, [])

  // プロジェクト削除
  const handleDeleteProject = useCallback((id: string) => {
    setProjects(prev => {
      const updated = prev.filter(p => p.id !== id)
      saveProjects(updated)
      return updated
    })
  }, [])

  // プロジェクト編集開始
  const handleEditProject = useCallback((project: Project) => {
    setEditingProject({ ...project })
  }, [])

  // プロジェクト編集保存
  const handleSaveEdit = useCallback(() => {
    if (!editingProject) return

    setProjects(prev => {
      const updated = prev.map(p =>
        p.id === editingProject.id ? editingProject : p
      )
      saveProjects(updated)
      return updated
    })
    setEditingProject(null)
  }, [editingProject])

  // プロジェクト編集キャンセル
  const handleCancelEdit = useCallback(() => {
    setEditingProject(null)
  }, [])

  const filteredProjects = useMemo(() => {
    if (!selectedTech) return projects
    return projects.filter(project =>
      project.techStack.includes(selectedTech)
    )
  }, [selectedTech, projects])

  return (
    <div className="py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-center text-3xl font-bold sm:text-4xl">Projects</h1>
        <p className="mx-auto mb-8 max-w-2xl text-center text-muted-foreground">
          GitHubリポジトリを連携して、あなたのプロジェクトを追加しましょう。
        </p>

        {/* Tech Filter - プロジェクトがある場合のみ表示 */}
        {projects.length > 0 && (
          <div className="mx-auto mb-8 flex max-w-3xl flex-wrap justify-center gap-2">
            <button
              onClick={() => handleTechSelect(null)}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                selectedTech === null
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              )}
            >
              全て
            </button>
            {allTechStacks.map((tech) => (
              <button
                key={tech}
                onClick={() => handleTechSelect(tech)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  selectedTech === tech
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                )}
              >
                {tech}
              </button>
            ))}
          </div>
        )}

        <div className="mx-auto grid max-w-5xl gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
            >
              {/* Thumbnail Image */}
              <div className="relative aspect-video w-full overflow-hidden bg-secondary">
                {project.imageUrl ? (
                  <img
                    src={project.imageUrl}
                    alt={project.title}
                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    {project.isAIGenerated ? (
                      <Sparkles className="h-12 w-12 text-primary" />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-muted-foreground" />
                    )}
                  </div>
                )}
                {/* AI生成バッジ */}
                {project.isAIGenerated && (
                  <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
                    <Sparkles className="h-3 w-3" />
                    AI生成
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col p-6">
                <div className="mb-2 flex items-start justify-between">
                  <h3 className="text-lg font-semibold">{project.title}</h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditProject(project)}
                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
                      title="編集"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      title="削除"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="mb-4 flex-1 text-sm text-muted-foreground">
                  {project.description}
                </p>
                <div className="mb-4 flex flex-wrap gap-2">
                  {project.techStack.map((tech) => (
                    <span
                      key={tech}
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                        selectedTech === tech
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                      )}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex gap-4">
                  {project.githubUrl ? (
                    <a
                      href={project.githubUrl}
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Github className="h-4 w-4" />
                      Code
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground/50">
                      <Github className="h-4 w-4" />
                      Code
                    </span>
                  )}
                  {project.demoUrl ? (
                    <a
                      href={project.demoUrl}
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Demo
                    </a>
                  ) : (
                    <Link
                      to={ROUTES.COMING_SOON}
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Demo
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="mx-auto max-w-md rounded-lg border border-dashed border-border bg-secondary/30 p-8 text-center">
            <Plus className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">プロジェクトがありません</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              下のフォームからGitHubリポジトリを選択して、プロジェクトを追加しましょう。
            </p>
          </div>
        )}

        {filteredProjects.length === 0 && projects.length > 0 && (
          <p className="text-center text-muted-foreground">
            該当するプロジェクトがありません
          </p>
        )}

        {/* AI Project Description Generator Section */}
        <div className="mx-auto mt-16 max-w-2xl">
          <h2 className="mb-6 text-center text-2xl font-bold">
            プロジェクト説明を自動生成
          </h2>
          <AIProjectDescGenerator onAddProject={handleAddProject} />
        </div>
      </div>

      {/* 編集モーダル */}
      {editingProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">プロジェクトを編集</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">タイトル</label>
                <input
                  type="text"
                  value={editingProject.title}
                  onChange={(e) =>
                    setEditingProject({ ...editingProject, title: e.target.value })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">説明</label>
                <textarea
                  value={editingProject.description}
                  onChange={(e) =>
                    setEditingProject({ ...editingProject, description: e.target.value })
                  }
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  技術スタック（カンマ区切り）
                </label>
                <input
                  type="text"
                  value={editingProject.techStack.join(', ')}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      techStack: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">GitHub URL</label>
                <input
                  type="url"
                  value={editingProject.githubUrl}
                  onChange={(e) =>
                    setEditingProject({ ...editingProject, githubUrl: e.target.value })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Demo URL</label>
                <input
                  type="url"
                  value={editingProject.demoUrl || ''}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      demoUrl: e.target.value || null,
                    })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">画像URL</label>
                <input
                  type="url"
                  value={editingProject.imageUrl || ''}
                  onChange={(e) =>
                    setEditingProject({
                      ...editingProject,
                      imageUrl: e.target.value || undefined,
                    })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleCancelEdit}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveEdit}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
